import React, { useRef, useCallback, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

/* ─────────────────────────────────────────────────────────────
   Inline SVG icons (zero external icon dependency)
   ───────────────────────────────────────────────────────────── */
export const Icon = ({ d, size = 16, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d={d} />
  </svg>
);

export const SpinnerIcon = ({ size = 16, className = '' }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

export const mkId = () => Math.random().toString(36).substring(2, 9);

/* Sentinel `toKey` meaning "create a brand-new module for these questions". */
export const NEW_MODULE_KEY = '__new__';

/* Interview.rubrics.questionsPerInterview max (server clamps to it on save). */
export const INTERVIEW_MAX_PICK = 10;

/* ─────────────────────────────────────────────────────────────
   "Mandatory" questions: always asked, whatever the random pick
   (server: modules/assessment/rubricPick.js). Shared by both rubric editors.
   ───────────────────────────────────────────────────────────── */
export const countMandatory = (bank) => (bank || []).filter((q) => q?.mandatory === true).length;

/* A module's pick count after its bank changed: never below its mandatory
   count, never above the bank size or `maxPick`. */
export function fitPick(pick, bank, { minPick = 1, maxPick = Infinity } = {}) {
  const ceiling = Math.max(minPick, Math.min((bank || []).length, maxPick));
  const floor = Math.min(Math.max(minPick, countMandatory(bank)), ceiling);
  const value = Number(pick);
  return Math.max(floor, Math.min(Number.isFinite(value) ? value : floor, ceiling));
}

/* Small checkbox in a question's header row. Clicks never toggle the card. */
export function MandatoryToggle({ checked, onChange, disabled = false, disabledReason = '' }) {
  return (
    <label
      onClick={(e) => e.stopPropagation()}
      title={disabled ? disabledReason : 'Ask this question to every candidate'}
      className={`flex items-center gap-1.5 text-xs select-none ${
        disabled ? 'text-gray-300 cursor-not-allowed' : checked ? 'text-blue-700 font-medium cursor-pointer' : 'text-gray-500 cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-400 disabled:cursor-not-allowed"
      />
      Mandatory
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────
   Pure helper: re-parent questions from one rubric module to another.

   Shared by this AI-interview editor and the rubric-based assessment editor
   (pages/assessments/RubricBasedAssessmentCreate.js), which name the same
   concepts differently:

     interview  → module key `_localId`, pick field `questionsPerInterview` (1…10)
     assessment → module key `id`,       pick field `questionsToPick`       (0…∞)

   Both editors send their whole modules array on save and the server rebuilds
   from it, so moving a question here is all the persistence this needs.

   Returns a NEW array and never mutates. Guarantees:
     - pick counts are re-clamped on BOTH sides, so a shrunken bank can never
       keep a stale "pick 8 of 5", and a bank that gained mandatory questions
       picks at least that many;
     - the source module is never left empty (save validation rejects empty
       banks, which would strand the user in an unsaveable form);
     - a newly created module always gets an id and a non-blank name — the
       server silently drops nameless rubrics / questions with no moduleId.
   ───────────────────────────────────────────────────────────── */
export function moveQuestions(
  modules,
  {
    fromIdx,
    toKey,
    questionIds,
    idKey = '_localId',
    pickKey = 'questionsPerInterview',
    minPick = 1,
    maxPick = Infinity,
    newModuleName = '',
    newModuleId,
  } = {}
) {
  if (!Array.isArray(modules)) return modules;

  const src = modules[fromIdx];
  if (!src || !Array.isArray(src.questionBank)) return modules;

  const ids = new Set(questionIds || []);
  if (ids.size === 0) return modules;

  const moved = src.questionBank.filter((q) => ids.has(q._localId));
  const kept = src.questionBank.filter((q) => !ids.has(q._localId));
  if (moved.length === 0) return modules;
  if (kept.length === 0) return modules; // would strand the source empty

  const isNew = toKey === NEW_MODULE_KEY;
  if (!isNew) {
    const dest = modules.find((m, i) => i !== fromIdx && m[idKey] === toKey);
    // Unknown / same-module destination, or one the server would drop for
    // having no name — either way, moving questions there would lose them.
    if (!dest || !String(dest.name || '').trim()) return modules;
  }

  const limits = { minPick, maxPick };
  const next = modules.map((m, i) => {
    if (i === fromIdx) {
      return { ...m, questionBank: kept, [pickKey]: fitPick(m[pickKey], kept, limits) };
    }
    if (!isNew && m[idKey] === toKey) {
      const questionBank = [...m.questionBank, ...moved];
      return { ...m, questionBank, [pickKey]: fitPick(m[pickKey], questionBank, limits) };
    }
    return m;
  });

  if (isNew) {
    const name = String(newModuleName || '').trim() || `Module ${modules.length + 1}`;
    next.push({
      [idKey]: newModuleId || mkId(),
      name,
      [pickKey]: fitPick(moved.length, moved, limits),
      questionBank: moved,
    });
  }

  return next;
}

/* ─────────────────────────────────────────────────────────────
   Sub-component: Question Card
   ───────────────────────────────────────────────────────────── */
export function QuestionCard({ question, moduleIndex, questionIndex, onUpdate, onRemove, isExpanded, onToggle, selectable = false, selected = false, onSelectToggle, onMandatoryChange, mandatoryLocked = false }) {
  const [generatingPoints, setGeneratingPoints] = useState(false);

  const handleGenerateExpectedPoints = async () => {
    if (!question.prompt) {
      toast.error('Please enter a question prompt first.');
      return;
    }
    setGeneratingPoints(true);
    try {
      const res = await axios.post('/api/ai-interviews/generate-expected', {
        question: {
          prompt: question.prompt,
          type: question.type || 'short',
          section: 'technical',
        },
      });
      
      const data = res.data;
      if (!data?.success) {
        toast.error(data?.message || 'No points generated. Try again.');
        return;
      }

      const points = data.result?.expectedPoints;
      if (Array.isArray(points) && points.length > 0) {
        onUpdate({ expectedPoints: points });
        toast.success('Generated answer points!');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error generating points:', err);
      toast.error('Failed to generate answer points. Try adding them manually.');
    } finally {
      setGeneratingPoints(false);
    }
  };

  const addPoint = () => {
    onUpdate({ expectedPoints: [...(question.expectedPoints || []), ''] });
  };
  const updatePoint = (idx, text) => {
    const pts = [...(question.expectedPoints || [])];
    pts[idx] = text;
    onUpdate({ expectedPoints: pts });
  };
  const removePoint = (idx) => {
    const pts = [...(question.expectedPoints || [])];
    pts.splice(idx, 1);
    onUpdate({ expectedPoints: pts });
  };

  return (
    <div className={`bg-white border rounded-lg shadow-sm overflow-hidden ${selected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'}`}>
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelectToggle && onSelectToggle()}
              aria-label={`Select question ${questionIndex + 1}`}
              className="flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer"
            />
          )}
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">
            Q{questionIndex + 1}
          </span>
          <span className="text-sm font-medium text-gray-700 truncate">
            {question.prompt || <span className="text-gray-400 italic">New question…</span>}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onMandatoryChange && (
            <MandatoryToggle
              checked={question.mandatory === true}
              onChange={onMandatoryChange}
              disabled={mandatoryLocked && question.mandatory !== true}
              disabledReason={`A module can have at most ${INTERVIEW_MAX_PICK} mandatory questions`}
            />
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50"
            title="Remove question"
          >
            <Icon d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={14} />
          </button>
          <Icon d={isExpanded ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} size={14} className="text-gray-400 ml-1" />
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Question text *</label>
          <textarea
            rows={2}
            value={question.prompt}
            onChange={(e) => onUpdate({ prompt: e.target.value })}
            placeholder="Type your question…"
            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select
              value={question.type}
              onChange={(e) => onUpdate({ type: e.target.value })}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="short">Open-ended</option>
              <option value="yes_no">Yes / No</option>
            </select>
          </div>
          {question.type === 'yes_no' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Expected answer</label>
              <select
                value={question.expectedAnswer}
                onChange={(e) => onUpdate({ expectedAnswer: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          )}
        </div>
        {question.type === 'short' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">
                Expected Answer Points (for scoring)
              </label>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleGenerateExpectedPoints}
                  disabled={generatingPoints}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  {generatingPoints ? <SpinnerIcon size={12} /> : <Icon d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM9 9l3-3 3 3M12 6v7" size={12} />}
                  Generate
                </button>
                <button
                  type="button"
                  onClick={addPoint}
                  className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <Icon d="M12 5v14M5 12h14" size={12} />
                  Add point
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              {(question.expectedPoints || []).map((pt, pIdx) => (
                <div key={pIdx} className="flex items-center gap-1.5">
                  <span className="flex-shrink-0 w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-500 font-medium">
                    {pIdx + 1}
                  </span>
                  <input
                    type="text"
                    value={pt}
                    onChange={(e) => updatePoint(pIdx, e.target.value)}
                    placeholder={`Answer point ${pIdx + 1}…`}
                    className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <button
                    type="button"
                    onClick={() => removePoint(pIdx)}
                    className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors"
                  >
                    <Icon d="M18 6L6 18M6 6l12 12" size={12} />
                  </button>
                </div>
              ))}
              {(question.expectedPoints || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">No points yet. Click "Generate" or "Add point".</p>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center gap-4">
          {question.type === 'yes_no' && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(question.critical)}
                onChange={(e) => onUpdate({ critical: e.target.checked })}
                className="rounded border-gray-300 text-red-500"
              />
              <span className="text-xs text-gray-600">Disqualifying (critical)</span>
            </label>
          )}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(question.allowFollowUps)}
              onChange={(e) => onUpdate({ allowFollowUps: e.target.checked })}
              className="rounded border-gray-300 text-blue-500"
            />
            <span className="text-xs text-gray-600">Allow follow-ups</span>
          </label>
        </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sub-component: "N selected → move to…" action bar

   Rendered inside a module once at least one of its questions is ticked.
   Shared by both rubric editors; it owns no module state, it just collects a
   destination and hands it back through onMove(toKey, newModuleName).
   ───────────────────────────────────────────────────────────── */
export function MoveQuestionsBar({ count, siblingModules = [], defaultNewName = 'New module', onMove, onClear, canMove = true, blockedReason = '' }) {
  const [target, setTarget] = useState('');
  const [newName, setNewName] = useState(defaultNewName);

  const isNew = target === NEW_MODULE_KEY;
  const nameOk = !isNew || newName.trim().length > 0;
  const disabled = !canMove || !target || !nameOk;

  const handleMove = () => {
    if (disabled) return;
    onMove(target, isNew ? newName.trim() : '');
    setTarget('');
    setNewName(defaultNewName);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <span className="text-xs font-semibold text-blue-800 whitespace-nowrap">
        {count} question{count !== 1 ? 's' : ''} selected
      </span>
      <span className="text-xs text-blue-400">→</span>
      <select
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        aria-label="Move selected questions to"
        className="text-xs border border-blue-300 rounded-md px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
      >
        <option value="">Move to…</option>
        {siblingModules.map((s) => (
          // A nameless module is dropped by the server on save, so it must not
          // be offered as a destination until it has been named.
          <option key={s.key} value={s.key} disabled={!String(s.name || '').trim()}>
            {String(s.name || '').trim() || 'Unnamed module — name it first'}
          </option>
        ))}
        <option value={NEW_MODULE_KEY}>+ New module…</option>
      </select>
      {isNew && (
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New module name…"
          aria-label="New module name"
          className="text-xs border border-blue-300 rounded-md px-2 py-1.5 bg-white w-40 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      )}
      <button
        type="button"
        onClick={handleMove}
        disabled={disabled}
        title={!canMove ? blockedReason : undefined}
        className="text-xs font-semibold px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Move
      </button>
      <button
        type="button"
        onClick={onClear}
        className="text-xs text-blue-700 hover:text-blue-900 px-2 py-1.5"
      >
        Clear
      </button>
      {!canMove && blockedReason && (
        <span className="text-xs text-amber-700 basis-full">{blockedReason}</span>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sub-component: Module Card
   ───────────────────────────────────────────────────────────── */
export function ModuleCard({ module, moduleIndex, onUpdate, onRemove, totalModules, interviewTitle, isExpanded, onToggle, showUploadButton = false, siblingModules = [], onMoveQuestions }) {
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleFileParse = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('questionsFile', file);
      fd.append('moduleName', module.name);
      fd.append('constraints', JSON.stringify({ title: interviewTitle }));
      const res = await axios.post('/api/ai-interviews/parse-module-questions', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const extractedQuestions = res.data?.questions;
      if (!Array.isArray(extractedQuestions) || extractedQuestions.length === 0) {
        toast.error('No questions could be extracted from the file for this module.');
        return;
      }
      
      const newQuestions = extractedQuestions.map((q) => ({
        _localId: mkId(),
        prompt: q.prompt || '',
        type: q.type || 'short',
        expectedAnswer: q.expectedAnswer || '',
        expectedPoints: q.expectedPoints || [],
        critical: Boolean(q.critical),
        allowFollowUps: q.allowFollowUps !== false,
      }));
      
      onUpdate({ questionBank: [...module.questionBank, ...newQuestions] });
      if (!isExpanded) onToggle();
      toast.success(`Added ${newQuestions.length} question(s) to ${module.name || 'this module'}.`);
    } catch (err) {
      console.error('File parse error:', err);
      const msg = err.response?.data?.message;
      if (err.response?.status === 429) {
        toast.error('AI service is busy. Please try again in a moment.');
      } else if (err.response?.status === 503) {
        toast.error('AI service is not configured on this server.');
      } else {
        toast.error(msg || 'Failed to parse the uploaded file. Please try again.');
      }
    } finally {
      setParsing(false);
    }
  };

  const addQuestion = () => {
    const newQ = {
      _localId: mkId(),
      prompt: '',
      type: 'short',
      expectedAnswer: '',
      expectedPoints: [],
      critical: false,
      allowFollowUps: true,
    };
    onUpdate({ questionBank: [...module.questionBank, newQ] });
    if (!isExpanded) onToggle();
    setExpandedQuestionId(newQ._localId);
  };

  const updateQuestion = (qIdx, patch) => {
    const qs = module.questionBank.map((q, i) => (i === qIdx ? { ...q, ...patch } : q));
    onUpdate({ questionBank: qs });
  };

  const pickLimits = { minPick: 1, maxPick: INTERVIEW_MAX_PICK };

  const removeQuestion = (qIdx) => {
    const removed = module.questionBank[qIdx];
    const questionBank = module.questionBank.filter((_, i) => i !== qIdx);
    // Pull the pick count down with the bank so it can never read "pick 8 of 5".
    onUpdate({ questionBank, questionsPerInterview: fitPick(module.questionsPerInterview, questionBank, pickLimits) });
    if (removed) setSelectedIds((prev) => prev.filter((id) => id !== removed._localId));
  };

  // Ticking raises the pick to cover every mandatory question; unticking keeps it.
  const setMandatory = (qIdx, mandatory) => {
    const questionBank = module.questionBank.map((q, i) => (i === qIdx ? { ...q, mandatory } : q));
    onUpdate({ questionBank, questionsPerInterview: fitPick(module.questionsPerInterview, questionBank, pickLimits) });
  };

  const bankSize = module.questionBank.length;
  const mandatoryCount = countMandatory(module.questionBank);
  const minPick = Math.max(1, Math.min(mandatoryCount, INTERVIEW_MAX_PICK));
  const maxPick = Math.max(minPick, Math.min(bankSize, INTERVIEW_MAX_PICK));

  /* ── Selection → "move to another module" ── */
  // Moving is only meaningful with somewhere to move to and something to leave behind.
  const canSelect = typeof onMoveQuestions === 'function' && bankSize > 1;
  const defaultNewModuleName = `Module ${totalModules + 1}`;

  const toggleSelected = (id) => {
    if (!id) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleMoveSelected = (toKey, newName) => {
    onMoveQuestions(selectedIds, toKey, newName);
    setSelectedIds([]);
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <div 
        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 cursor-pointer"
        onClick={onToggle}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} module ${module.name || moduleIndex + 1}`}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {isExpanded ? (
            <Icon d="M5 15l7-7 7 7" size={18} />
          ) : (
            <Icon d="M19 9l-7 7-7-7" size={18} />
          )}
        </button>
        <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={module.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="Module name (e.g. Sales, Leadership, Eligibility)…"
            className="w-full text-sm font-semibold bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-800 placeholder-gray-400"
          />
          <p className="text-xs text-gray-500 mt-0.5">
            {bankSize} question{bankSize !== 1 ? 's' : ''} in bank
            {mandatoryCount > 0 && <span className="text-blue-700"> · {mandatoryCount} mandatory</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {showUploadButton && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileParse(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                disabled={parsing}
                onClick={() => {
                  if (!module.name.trim()) {
                    toast.error("Please enter a module name first so we know what kind of questions to extract.");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                  parsing ? "text-gray-400 opacity-50" : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                }`}
                title="Upload document to extract questions (PDF, DOC, DOCX)"
              >
                {parsing ? <SpinnerIcon size={18} /> : <Icon d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12" size={18} />}
              </button>
            </>
          )}
          
          <div className="flex flex-col items-end">
            <span className="text-xs text-gray-500 mb-0.5">Pick per interview</span>
            <div className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-lg px-2 py-1 shadow-sm">
              <button
                type="button"
                onClick={() => onUpdate({ questionsPerInterview: Math.max(minPick, module.questionsPerInterview - 1) })}
                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              >
                <Icon d="M5 12h14" size={12} />
              </button>
              <input
                type="number"
                min={minPick}
                max={maxPick}
                value={module.questionsPerInterview === '' ? '' : module.questionsPerInterview}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val === '') {
                    onUpdate({ questionsPerInterview: '' });
                    return;
                  }
                  val = parseInt(val, 10);
                  if (!isNaN(val)) {
                    onUpdate({ questionsPerInterview: Math.min(maxPick, Math.max(minPick, val)) });
                  }
                }}
                onBlur={() => {
                  if (module.questionsPerInterview === '' || module.questionsPerInterview < minPick) {
                    onUpdate({ questionsPerInterview: minPick });
                  }
                }}
                className="w-8 text-center text-sm font-bold text-blue-700 bg-transparent border-none p-0 focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                style={{ MozAppearance: 'textfield' }}
              />
              <button
                type="button"
                onClick={() => onUpdate({ questionsPerInterview: Math.min(maxPick, module.questionsPerInterview + 1) })}
                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              >
                <Icon d="M12 5v14M5 12h14" size={12} />
              </button>
            </div>
          </div>
        </div>
        {totalModules > 1 && (
          <button
            type="button"
            onClick={onRemove}
            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove module"
          >
            <Icon d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={16} />
          </button>
        )}
      </div>
      {isExpanded && (
        <div className="p-4 space-y-3">
          {module.questionBank.length === 0 && (
            <div className="text-center py-6 text-gray-400">
              <Icon d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" size={28} className="mx-auto mb-2" />
              <p className="text-sm">No questions yet. Add some below.</p>
            </div>
          )}
          {selectedIds.length > 0 && (
            <MoveQuestionsBar
              count={selectedIds.length}
              siblingModules={siblingModules}
              defaultNewName={defaultNewModuleName}
              canMove={selectedIds.length < bankSize}
              blockedReason="Keep at least one question here — rename this module instead of emptying it."
              onClear={() => setSelectedIds([])}
              onMove={handleMoveSelected}
            />
          )}
          {module.questionBank.map((q, qIdx) => (
            <QuestionCard
              key={q._localId || qIdx}
              question={q}
              moduleIndex={moduleIndex}
              questionIndex={qIdx}
              onUpdate={(patch) => updateQuestion(qIdx, patch)}
              onRemove={() => removeQuestion(qIdx)}
              isExpanded={expandedQuestionId === (q._localId || qIdx)}
              onToggle={() => setExpandedQuestionId(expandedQuestionId === (q._localId || qIdx) ? null : (q._localId || qIdx))}
              selectable={canSelect && Boolean(q._localId)}
              selected={selectedIds.includes(q._localId)}
              onSelectToggle={() => toggleSelected(q._localId)}
              onMandatoryChange={(checked) => setMandatory(qIdx, checked)}
              mandatoryLocked={mandatoryCount >= INTERVIEW_MAX_PICK}
            />
          ))}
          <button
            type="button"
            onClick={addQuestion}
            className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
          >
            <Icon d="M12 5v14M5 12h14" size={14} />
            Add question
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main component: RubricModulesEditor
   ───────────────────────────────────────────────────────────── */
export default function RubricModulesEditor({ modules, setModules, interviewTitle, showUploadButton = false }) {
  const [expandedModuleId, setExpandedModuleId] = useState(null);

  const addModule = () => {
    const newId = mkId();
    setModules((prev) => [
      ...prev,
      {
        _localId: newId,
        // Pre-named: the server silently drops rubrics whose name is blank.
        name: `Module ${prev.length + 1}`,
        questionsPerInterview: 1,
        questionBank: [],
      },
    ]);
    setExpandedModuleId(newId);
  };

  const updateModule = (idx, patch) => {
    setModules((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  // Re-parent questions out of module `fromIdx`. One atomic update so the
  // source and destination banks can never be written from a stale array.
  const handleMoveQuestions = (fromIdx, questionIds, toKey, newModuleName) => {
    const moving = (modules[fromIdx]?.questionBank || []).filter((q) => questionIds.includes(q._localId));
    const destBank = toKey === NEW_MODULE_KEY ? [] : modules.find((m) => m._localId === toKey)?.questionBank || [];
    if (countMandatory([...destBank, ...moving]) > INTERVIEW_MAX_PICK) {
      toast.error(`A module can have at most ${INTERVIEW_MAX_PICK} mandatory questions. Untick some before moving.`);
      return;
    }
    const newModuleId = toKey === NEW_MODULE_KEY ? mkId() : null;
    setModules((prev) =>
      moveQuestions(prev, {
        fromIdx,
        toKey,
        questionIds,
        idKey: '_localId',
        pickKey: 'questionsPerInterview',
        minPick: 1,
        maxPick: INTERVIEW_MAX_PICK,
        newModuleName,
        newModuleId,
      })
    );
    if (newModuleId) setExpandedModuleId(newModuleId);
  };

  const removeModule = (idx) => {
    setModules((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-5">
      {/* Interview Title Display */}
      {interviewTitle && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 flex items-start gap-3">
          <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" size={20} className="text-gray-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">Interview Title</p>
            <p className="text-sm font-semibold text-gray-900">{interviewTitle}</p>
          </div>
        </div>
      )}

      {/* How it works banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Icon d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-0.5">How it works</p>
          <p className="text-xs leading-relaxed">
            Each module is a question bank. For every candidate, the system randomly picks the configured number of questions from each module and gives that flat list to the AI interviewer. Candidates always get a fresh, different set. Questions marked Mandatory are asked to every candidate; the rest of the pick is random.
          </p>
        </div>
      </div>

      {/* Modules */}
      <div className="space-y-4">
        {modules.map((m, idx) => (
          <ModuleCard
            key={m._localId}
            module={m}
            moduleIndex={idx}
            onUpdate={(patch) => updateModule(idx, patch)}
            onRemove={() => removeModule(idx)}
            totalModules={modules.length}
            interviewTitle={interviewTitle}
            isExpanded={expandedModuleId === m._localId}
            onToggle={() => setExpandedModuleId(expandedModuleId === m._localId ? null : m._localId)}
            showUploadButton={showUploadButton}
            siblingModules={modules
              .filter((s) => s._localId !== m._localId)
              .map((s) => ({ key: s._localId, name: s.name }))}
            onMoveQuestions={(ids, toKey, newName) => handleMoveQuestions(idx, ids, toKey, newName)}
          />
        ))}
      </div>

      {/* Add module manually */}
      <button
        type="button"
        onClick={addModule}
        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all font-medium"
      >
        <Icon d="M12 5v14M5 12h14" size={16} />
         Create Module Manually
      </button>

      {/* Summary card */}
      {modules.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-2">Interview Summary</p>
          <div className="space-y-1.5">
            {modules.map((m) => (
              <div key={m._localId} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">
                  <span className="font-medium">{m.name || 'Unnamed module'}</span>
                  <span className="text-gray-400"> ({m.questionBank.length} questions in bank)</span>
                </span>
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                  Pick {m.questionsPerInterview}
                </span>
              </div>
            ))}
            <div className="pt-1.5 border-t border-gray-200 mt-1.5 flex items-center justify-between text-xs font-medium text-gray-700">
              <span>Total questions per interview</span>
              <span className="text-blue-700">
                {modules.reduce((sum, m) => sum + Math.min(m.questionsPerInterview, m.questionBank.length), 0)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
